<template>
  <PageStyle>
    <template #pageHeader>
      <router-link
        :to="{
          name: 'applications/landing',
          params: { applicationId: applicationId },
        }"
        class="inline-flex items-center text-sm text-gray-700 hover:text-gray-700 hover:underline"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          class="feather feather-chevron-left w-4 h-4"
        >
          <polyline points="15 18 9 12 15 6" />
        </svg>
        <span class="ml-1">Back</span>
      </router-link>
    </template>
    <template #pageContent>
      <FormPanel
        :id="'polimodaRegulations'"
        :title="'Polimoda Student Regulations Policy'"
        class="mt-6"
        panel-content-class="text-gray-600 border-gray-200"
      >
        <template #content>
          <form
            id="polimoda-regulations-form"
            class="mb-6"
            action=""
            aria-label="Polimoda Regulations"
            @submit.prevent="submitForm()"
          >
            <div class="mb-6 space-y-6">
              <p class="mb-6">
                Polimoda, the host institution for your program, requires you to
                review their registration policy, hand sign, and upload a copy
                of the signed document here to your application.
              </p>
              <a
                href="/files/SU20_Polimoda_Regulations.pdf"
                target="_blank"
                :class="`${secondaryColorClass} font-semibold hover:underline`"
              >
                Click here to download the Polimoda Student Regulations Policy
              </a>
              <p>
                Upload the Polimoda Student Regulations Policy once you have
                hand signed the document.
              </p>
            </div>

            <FileUpload
              name="polimoda-regulations-upload"
              :formio-files="formioFiles"
              :multiple-files="false"
              :file-types="['*']"
              @updateProgress="updateProgress"
              @updateFiles="updateFiles"
            />

            <ButtonWithSpinner
              ref="polimodaSubmit"
              type="submit"
              variant="primary"
              variant-type="block"
              :disabled="v$.$invalid"
            >
              <span>
                Submit
                <svg
                  v-if="successSubmission && !v$.$invalid"
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  class="inline feather feather-check"
                >
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
              </span>
            </ButtonWithSpinner>
            <div v-if="submitError" class="error text-error-900 mt-2">
              {{ submitError }}
            </div>
            <div
              v-if="successSubmission && !v$.$invalid"
              class="text-success-900"
            >
              Submission Complete, thank you!
            </div>
          </form>
        </template>
      </FormPanel>
    </template>
  </PageStyle>
</template>

<script>
import useVuelidate from "@vuelidate/core";
import { required } from "@vuelidate/validators";
import formIoApi from "../../../mixins/formIoApi.js";
import forms from "../../../mixins/forms.js";
import ButtonWithSpinner from "../SharedComponents/ButtonWithSpinner.vue";
import FileUpload from "../SharedComponents/FileUpload.vue";
import PageStyle from "../SharedComponents/Layout/PageDefault.vue";
import FormPanel from "../SharedComponents/panel.vue";

const validateFileUpload = (value) => value === false;

export default {
  name: "PolimodaRegulationsPolicy",
  components: {
    FormPanel,
    PageStyle,
    FileUpload,
    ButtonWithSpinner,
  },
  mixins: [formIoApi, forms],
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      value: {
        files: [],
      },
      uploadOnProgress: false,
      uploadErrors: false,
      successSubmission: false,
      formioFiles: [],
      submitError: "",
      formURL: "polimodastudentprivacypolicy",
      submissionId: "",
    };
  },
  validations: {
    value: {
      files: { required },
    },
    uploadOnProgress: { validateFileUpload },
    uploadErrors: { validateFileUpload },
  },
  computed: {
    applicationId() {
      return this.$route.params.applicationId;
    },
  },
  async created() {
    const valid = await this.isValidApplication(
      this.applicationId,
      "Polimoda Student Regulations Policy",
      "post_acceptance_form_rules",
    );

    if (valid) {
      this.getFormioData();
    } else {
      this.$router.push({ name: "experiences" });
    }
  },

  methods: {
    submitForm() {
      this.v$.$touch();
      this.submitError = "";
      this.successSubmission = false;

      if (!this.v$.$invalid) {
        this.$refs.polimodaSubmit.startLoading();

        if (this.submissionId) {
          this.updateSubmission();
        } else {
          this.createSubmission();
        }
      }
    },
    updateProgress(val) {
      this.uploadOnProgress = val;
    },
    updateFiles(files, error) {
      this.value.files = files;
      this.uploadErrors = error;
      this.successSubmission = false;
    },
    getFormioData() {
      this.formioSubmissionExists(this.formURL, this.applicationId).then((submissionId) => {
        this.submissionId = submissionId;

        if (this.submissionId) {
          this.getFormioSubmission(this.formURL, this.submissionId).then((response) => {
            this.formioFiles = response.polimodaFile ? response.polimodaFile : [];
          });
        }
      });
    },
    getJsonData() {
      return {
        data: {
          application_id: this.applicationId,
          polimodaFile: this.value.files,
        },
      };
    },
    createSubmission() {
      this.submitToFormIo(this.formURL, this.getJsonData())
        .then((response) => {
          this.submissionId = response;
          this.successSubmission = true;
        })
        .catch((error) => {
          this.submitError = error;
        })
        .finally(() => {
          this.$refs.polimodaSubmit.stopLoading();
        });
    },
    updateSubmission() {
      this.submitToFormIo(this.formURL, this.getJsonData(), "PUT", this.submissionId)
        .then(() => {
          this.successSubmission = true;
        })
        .catch((error) => {
          this.submitError = error;
        })
        .finally(() => {
          this.$refs.polimodaSubmit.stopLoading();
        });
    },
  },
};
</script>
